import { getScrollParent } from '@react-aria/utils';
import { useVirtualizer, type VirtualItem, type Virtualizer } from '@tanstack/react-virtual';
import * as React from 'react';

import { useLiveRef } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

export type MenuVirtualRendererItem = { id: string };

export type MenuVirtualRendererRef = {
    scrollToId: (id: string) => void;
    scrollToTop: () => void;
};

export type MenuVirtualRendererProps<T extends MenuVirtualRendererItem> = {
    items: T[];
    onMeasureWidth: (width: number) => void;
    /**
     * Width at which measuring more items cannot change the result, letting the initial
     * estimate pass stop early on long lists.
     */
    maxMeasuredWidth?: number;
    estimateSize: (index: number) => { width: number; height: number };
    renderItem: (
        virtualItem: VirtualItem,
        item: T,
        virtualizer: Virtualizer<Element, Element>
    ) => React.ReactNode;
};

export const MenuVirtualRenderer = function MenuVirtualRenderer<T extends MenuVirtualRendererItem>({
    ref,
    ...props
}: MenuVirtualRendererProps<T> & { ref?: React.ForwardedRef<MenuVirtualRendererRef> }) {
    const { items, renderItem, estimateSize, onMeasureWidth, maxMeasuredWidth } = props;
    const contentRef = React.useRef<HTMLDivElement>(null);
    const refs = useLiveRef({ onMeasureWidth, estimateSize });
    // Report a single widest estimate rather than one per item: a search over a large content
    // palette hands us thousands of items, and calling back per item queued a state update per
    // item inside a layout effect — thousands of scheduled updates blocking paint on every
    // keystroke. Stop early once the ceiling is reached, since nothing wider can change the
    // outcome.
    React.useLayoutEffect(() => {
        if (items.length === 0) {
            return;
        }

        const { onMeasureWidth, estimateSize } = refs.current;
        let widestWidth = 0;
        for (let i = 0; i < items.length; i++) {
            const { width } = estimateSize(i);
            if (width > widestWidth) {
                widestWidth = width;
                if (maxMeasuredWidth !== undefined && widestWidth >= maxMeasuredWidth) {
                    break;
                }
            }
        }
        onMeasureWidth(widestWidth);
    }, [items, refs, maxMeasuredWidth]);
    const virtualizer = useVirtualizer({
        count: items.length,
        getScrollElement: () => (contentRef.current ? getScrollParent(contentRef.current) : null),
        estimateSize: (index) => estimateSize(index).height,
        overscan: 5,
        paddingStart: 4,
        paddingEnd: 4,
        // Must mirror the menu's `scroll-pt-*`: scroll padding moves the browser's snap position,
        // and scrollToIndex retries for ~500ms when its target disagrees by more than a pixel.
        scrollPaddingStart: 4,
        getItemKey: (index) => {
            assert(items[index]);
            return items[index].id;
        },
        measureElement: (element) => {
            const rect = element.getBoundingClientRect();
            const width = rect.width;
            onMeasureWidth(width);
            return rect.height;
        },
    });
    const { scrollToIndex, scrollToOffset } = virtualizer;
    const itemsRef = useLiveRef(items);
    React.useImperativeHandle(
        ref,
        () => ({
            scrollToId: (id) => {
                const items = itemsRef.current;
                const index = items.findIndex((i) => i.id === id);
                if (index !== -1) {
                    scrollToIndex(index);
                }
            },
            scrollToTop: () => {
                // Offset scrolling is synchronous. scrollToIndex re-verifies its target over the
                // following animation frames, which throws if the virtualizer is torn down in
                // between — as it is when a search leaves the menu with no matches.
                scrollToOffset(0);
            },
        }),
        [scrollToIndex, scrollToOffset, itemsRef]
    );
    const virtualItems = virtualizer.getVirtualItems();
    return (
        <div
            ref={contentRef}
            className="relative"
            style={{
                height: virtualizer.getTotalSize(),
            }}
        >
            <div
                className="flex min-w-full flex-col [&>*]:snap-start"
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
                }}
            >
                {virtualItems.map((virtualItem) => {
                    const item = items[virtualItem.index];
                    if (!item) {
                        return null;
                    }
                    return renderItem(virtualItem, item, virtualizer);
                })}
            </div>
        </div>
    );
} as <T extends MenuVirtualRendererItem>(
    props: MenuVirtualRendererProps<T> & { ref?: React.ForwardedRef<MenuVirtualRendererRef> }
) => React.ReactElement;
